import { PackageCodes } from 'src/contexts/SubscriptionsContext';
import { getPackageByCode } from 'src/pages/saasSubscribe/utils/packages-catalog';

//
// Every onboarding page has the exact same shape: a title and an ordered
// list of blocks. What varies is the blocks themselves, not the page.
//

export type OnboardingPage = {
  title: string;
  blocks: Block[];
};

export type Block = ParagraphBlock | BulletsBlock | StepsBlock | CtaBlock;

export type ParagraphBlock = { type: 'paragraph'; text: string; secondary?: boolean };

export type BulletsBlock = { type: 'bullets'; intro?: string; items: string[] };

export type Step = { label: string; blocks: Block[] };

export type StepsBlock = { type: 'steps'; steps: Step[] };

export type CtaBlock = { type: 'cta'; primaryLabel: string; secondaryLabel?: string };

//
//

export const ONBOARDING_PAGES: Partial<Record<PackageCodes, OnboardingPage[]>> = {
  [PackageCodes.EVENT_OFFSETTING]: [
    {
      title: 'Turn your events into a sustainability story.',
      blocks: [
        {
          type: 'paragraph',
          text: `Welcome to CarbonClick. You've signed up for ${getPackageByCode(PackageCodes.EVENT_OFFSETTING)} — a complete toolkit for measuring, offsetting, and communicating the carbon and offsetting impact of your events.`,
        },
        { type: 'paragraph', text: "Here's how it works.", secondary: true },
      ],
    },
    {
      title: 'Start by creating a Programme.',
      blocks: [
        {
          type: 'paragraph',
          text: "A Programme is how you organise a specific sustainability initiative in CarbonClick. Everything related to that initiative lives inside it — the emissions you've calculated, the offsets you've purchased, and the landing page you share with stakeholders.",
        },
        { type: 'paragraph', text: 'One Programme. One story.', secondary: true },
        {
          type: 'bullets',
          intro: 'You might create a Programme for:',
          items: [
            'A recurring conference you host at a specific venue',
            'All events held at a particular property or location',
            'Events you manage on behalf of a client',
            'A dedicated initiative you want to have its own landing page and impact report',
          ],
        },
        {
          type: 'paragraph',
          text: 'You can create as many Programmes as you need.',
          secondary: true,
        },
      ],
    },
    {
      title: 'Three steps, from footprint to story.',
      blocks: [
        { type: 'paragraph', text: 'Click through each step to see how it works.', secondary: true },
        {
          type: 'steps',
          steps: [
            {
              label: 'Calculate',
              blocks: [
                {
                  type: 'paragraph',
                  text: 'Use the event calculator to measure carbon emissions across every part of your event — energy use, delegate travel, staff travel, food, and freight.',
                },
                {
                  type: 'paragraph',
                  text: 'If your Programme covers multiple events over time, you can add as many footprint calculations as you need. Each one is saved separately and rolls up into your Programme total.',
                  secondary: true,
                },
              ],
            },
            {
              label: 'Offset',
              blocks: [
                {
                  type: 'paragraph',
                  text: 'Choose from a curated portfolio of independently verified carbon offsetting projects — from forest conservation to clean cookstoves to renewable energy.',
                },
                {
                  type: 'paragraph',
                  text: 'Select the projects that align with your values, purchase your offset, and a certificate is generated automatically. You can configure a preferred project selection for your Programme so repeat offsets stay fast.',
                  secondary: true,
                },
              ],
            },
            {
              label: 'Communicate',
              blocks: [
                {
                  type: 'paragraph',
                  text: "Every Programme can have its own landing page, allowing you to tell the story of your Programme and sustainability initiatives. It shows the total emissions you've offset, the projects you've supported, and your cumulative impact over time — updating automatically as you add more offsets.",
                },
                {
                  type: 'paragraph',
                  text: 'You can also add user tools such as a travel calculator, so delegates or guests can measure and offset their own travel emissions directly from your page.',
                  secondary: true,
                },
                {
                  type: 'paragraph',
                  text: 'Share it with clients, include it in proposals, or link it from your website. Your sustainability story, always up to date.',
                  secondary: true,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      title: "You're ready to go.",
      blocks: [
        {
          type: 'paragraph',
          text: 'Start by creating your first Programme. Give it a name — you can always rename it later — and everything else follows from there.',
        },
        { type: 'cta', primaryLabel: 'Create your first Programme', secondaryLabel: "I'll explore first" },
      ],
    },
  ],

  [PackageCodes.BUSINESS_OFFSETTING]: [
    {
      title: 'Turn your business activity into climate action.',
      blocks: [
        {
          type: 'paragraph',
          text: `Welcome to CarbonClick. You've signed up for ${getPackageByCode(PackageCodes.BUSINESS_OFFSETTING)} — a complete toolkit for measuring, offsetting, and reporting on your organisation's carbon footprint.`,
        },
        { type: 'paragraph', text: "Here's how it works.", secondary: true },
      ],
    },
    {
      title: 'Start by creating a Programme.',
      blocks: [
        {
          type: 'paragraph',
          text: "A Programme is how you organise your offsetting activity in CarbonClick. Everything related to it lives inside — the emissions you've calculated, the offsets you've purchased, and the reporting you generate.",
        },
        {
          type: 'bullets',
          intro: 'You might create a Programme for:',
          items: [
            "Your organisation's recurring footprint",
            'A specific business unit, site, or division',
            'A reporting period you want tracked separately',
          ],
        },
      ],
    },
    {
      title: 'Three steps, from footprint to story.',
      blocks: [
        { type: 'paragraph', text: 'Click through each step to see how it works.', secondary: true },
        {
          type: 'steps',
          steps: [
            {
              label: 'Calculate',
              blocks: [
                {
                  type: 'paragraph',
                  text: "Click 'Create footprint' to use the business calculator to measure carbon emissions across your operations — energy use, travel, freight, and more.",
                },
                {
                  type: 'paragraph',
                  text: 'Add as many footprint calculations as you need over time. Each one is saved separately and rolls up into your Programme total.',
                  secondary: true,
                },
              ],
            },
            {
              label: 'Offset',
              blocks: [
                {
                  type: 'paragraph',
                  text: 'Choose from a curated portfolio of independently verified carbon offsetting projects — from forest conservation to clean cookstoves to renewable energy.',
                },
                {
                  type: 'paragraph',
                  text: 'Select the projects that align with your values, purchase your offset, and a certificate is generated automatically.',
                  secondary: true,
                },
              ],
            },
            {
              label: 'Communicate',
              blocks: [
                {
                  type: 'paragraph',
                  text: "Your Programme can have its own landing page, so you can share your sustainability story with clients, stakeholders, or your team. It shows your total emissions offset, the projects you've supported, and your cumulative impact — updating automatically.",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      title: "You're ready to go.",
      blocks: [
        {
          type: 'paragraph',
          text: 'Start by creating your first Programme. Give it a name — you can always rename it later — and everything else follows from there.',
        },
        { type: 'cta', primaryLabel: 'View your programmes' },
      ],
    },
  ],

  [PackageCodes.TRAVEL_TOURS]: [
    {
      title: "Offset your clients' journeys, start to finish.",
      blocks: [
        {
          type: 'paragraph',
          text: `Welcome to CarbonClick. You've signed up for ${getPackageByCode(PackageCodes.TRAVEL_TOURS)} — bulk upload flight data, calculate itinerary footprints, and issue certificates to your corporate clients, all from one portal.`,
        },
        { type: 'paragraph', text: "Here's how it works.", secondary: true },
      ],
    },
    {
      title: 'Start by creating a Programme.',
      blocks: [
        {
          type: 'paragraph',
          text: "A Programme is how you organise your offsetting activity in CarbonClick. Everything for a client or tour lives inside it — the flights you've calculated, the offsets you've purchased, and the certificates you issue.",
        },
        {
          type: 'bullets',
          intro: 'You might create a Programme for:',
          items: [
            'A specific corporate client account',
            'A recurring tour or travel contract',
            'A reporting period you want tracked separately',
          ],
        },
        {
          type: 'paragraph',
          text: 'You can create as many Programmes as you need — one per client or tour.',
          secondary: true,
        },
      ],
    },
    {
      title: 'Three steps, from itinerary to story.',
      blocks: [
        { type: 'paragraph', text: 'Click through each step to see how it works.', secondary: true },
        {
          type: 'steps',
          steps: [
            {
              label: 'Calculate',
              blocks: [
                {
                  type: 'paragraph',
                  text: "Upload your client's flight itineraries in bulk. CarbonClick calculates the carbon footprint for every journey automatically — no manual entry, no per-flight lookups.",
                },
              ],
            },
            {
              label: 'Offset',
              blocks: [
                {
                  type: 'paragraph',
                  text: 'Choose from a curated portfolio of independently verified offsetting projects — forest conservation, clean cookstoves, renewable energy. Purchase your offset, and a certificate is generated automatically for your client.',
                },
              ],
            },
            {
              label: 'Communicate',
              blocks: [
                {
                  type: 'paragraph',
                  text: "Every Programme has its own landing page, so your client can see a clear record of what's been offset on their behalf — updating automatically as you add more itineraries.",
                },
              ],
            },
          ],
        },
      ],
    },
    {
      title: "You're ready to go.",
      blocks: [
        {
          type: 'paragraph',
          text: 'Start by creating a Programme for your first client or tour. Give it a name — you can always rename it later.',
        },
        { type: 'cta', primaryLabel: 'Create your first Programme', secondaryLabel: "I'll explore first" },
      ],
    },
  ],

  [PackageCodes.ACCOMMODATION_OFFSETTING]: [
    {
      title: 'Turn every stay into a climate contribution.',
      blocks: [
        {
          type: 'paragraph',
          text: `Welcome to CarbonClick. You've signed up for ${getPackageByCode(PackageCodes.ACCOMMODATION_OFFSETTING)} — offer carbon-neutral stays for your guests. Upload your room data monthly, and let guests offset their own stay too.`,
        },
        { type: 'paragraph', text: "Here's how it works.", secondary: true },
      ],
    },
    {
      title: 'Start by creating a Programme.',
      blocks: [
        {
          type: 'paragraph',
          text: 'A Programme is how you organise your offsetting activity in CarbonClick. Everything lives inside it — the room data you upload, the offsets you purchase, and the guest-facing page you share.',
        },
        {
          type: 'bullets',
          intro: 'You might create a Programme for:',
          items: [
            'A single property',
            'A group of properties under one brand',
            'A specific reporting period',
          ],
        },
      ],
    },
    {
      title: 'Two ways to offset, one Programme.',
      blocks: [
        { type: 'paragraph', text: 'Click through each step to see how it works.', secondary: true },
        {
          type: 'steps',
          steps: [
            {
              label: 'Calculate',
              blocks: [
                {
                  type: 'paragraph',
                  text: 'Upload your monthly room data in bulk. CarbonClick calculates the footprint of every stay automatically.',
                },
              ],
            },
            {
              label: 'Offset',
              blocks: [
                {
                  type: 'paragraph',
                  text: 'Offset stays yourself from a portfolio of verified projects — or add a guest offset widget to your booking confirmation page, so guests can choose to offset their own stay directly. Use either, or both.',
                },
              ],
            },
            {
              label: 'Communicate',
              blocks: [
                {
                  type: 'paragraph',
                  text: 'Your Programme has its own landing page, so guests can see the impact of their stay and yours — updating automatically as you upload more data.',
                },
              ],
            },
          ],
        },
      ],
    },
    {
      title: "You're ready to go.",
      blocks: [
        {
          type: 'paragraph',
          text: 'Start by creating your first Programme. Give it a name — you can always rename it later.',
        },
        { type: 'cta', primaryLabel: 'Create your first Programme', secondaryLabel: "I'll explore first" },
      ],
    },
  ],
};

export const hasOnboardingContent = (packageCode?: string) =>
  !!packageCode && !!ONBOARDING_PAGES[packageCode as PackageCodes];
